import { Box, Flex, Text } from "@chakra-ui/react";
import {
	AccordionItem,
	AccordionButton,
	AccordionPanel,
	AccordionIcon,
} from "@chakra-ui/react";
import { useEarmoldsPaginated } from "../../../hooks/earmolds.hooks";
import { useNewOrderStore } from "../../../stores/order.store";
import { fetchEarmoldVariants } from "../../../queries/earmold.queries";
import { type Earmold, EarmoldCategory } from "../../../types/earmold.types";
import { useFile } from "../../../hooks/file.hooks";
import { cn } from "../../../lib/utils";
import BrandFullCheckIcon from "../../../components/icons/BrandFullCheckIcon";
import { useTranslation } from "react-i18next";
import Tooltip from "../../../components/Tooltip";
import CircleHelpIcon from "../../../components/icons/CircleHelpIcon";

const EarmoldSection = ({
	earmold,
	value,
	setValue,
}: {
	earmold: Earmold;
	value: string | undefined | null;
	setValue: (earmold: Earmold | undefined | null) => Promise<void>;
}) => {
	const { data: file } = useFile({ id: earmold.fileId });
	const isActive = value === earmold.id.toString();

	return (
		<Box
			key={earmold.id}
			className={cn(
				"relative",
				"bg-white flex flex-col items-center justify-center py-4 rounded-xl border! border-[#EAECF0]! cursor-pointer",
				{ "border-brand-600! border-2!": isActive },
			)}
			onClick={() => setValue(earmold)}
			onKeyDown={() => setValue(earmold)}
		>
			<img
				src={file?.url}
				alt=""
				className="w-[140px] max-w-[140px] aspect-square"
			/>
			<Flex alignItems="center" gap={1}>
				<Text fontSize="14px" fontWeight={500}>
					{earmold.label}
				</Text>
				{earmold.description && (
					<Tooltip content={earmold.description}>
						<Box display="flex" alignItems="center" lineHeight={1}>
							<CircleHelpIcon w="14px" h="14px" color="brand.600" />
						</Box>
					</Tooltip>
				)}
			</Flex>
			{isActive && (
				<BrandFullCheckIcon
					textColor="brand.600"
					position="absolute"
					top="10px"
					right="10px"
				/>
			)}
		</Box>
	);
};

const EarmoldsSectionPerCategory = ({
	category,
	earmolds,
	value,
	setValue,
}: {
	category: EarmoldCategory;
	earmolds: Array<Earmold> | undefined | null;
	value: string | undefined | null;
	setValue: (earmold: Earmold | undefined | null) => Promise<void>;
}) => {
	const { t } = useTranslation();

	if (!earmolds || earmolds.length === 0) return null;
	return (
		<Flex direction="column" gap={2} py={2}>
			<Text fontSize="12px" fontWeight={600} textColor="gray.500">
				{t(`earmold.category.${category}`)}
			</Text>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				{earmolds.map((earmold) => (
					<EarmoldSection
						key={earmold.id}
						earmold={earmold}
						value={value}
						setValue={setValue}
					/>
				))}
			</div>
		</Flex>
	);
};

export const EarSectionType = () => {
	const {
		setEarmold,
		earmold: selectedEarmold,
		toggleFormIndex,
	} = useNewOrderStore();
	// NOTE: No pagination required here (at least at short term) so use an arbitrary high value (100_000)
	const earmolds = useEarmoldsPaginated(1, 100_000, undefined, {
		status: "active",
	});

	if (earmolds === undefined || earmolds === null) {
		return <></>;
	}
	const earmoldLabel = earmolds.data.filter(
		(e) => e.id.toString() === selectedEarmold,
	)[0]?.label;
	const earmoldPerStatus = earmolds.data.reduce(
		(acc, earmold) => {
			if (earmold.category in acc) {
				acc[earmold.category].push(earmold);
			} else {
				acc[earmold.category] = [earmold];
			}
			return acc;
		},
		{} as Record<EarmoldCategory, Array<Earmold>>,
	);
	return (
		<AccordionItem>
			{({ isExpanded }) => (
				<>
					<AccordionButton onClick={() => toggleFormIndex(0)}>
						<Flex justifyContent="space-between" w="full">
							<Flex alignItems="center" pb={2} gap={2}>
								<Text lineHeight="21px" fontSize="16px" fontWeight="600">
									Type d'embout
								</Text>
								{earmoldLabel && !isExpanded && (
									<Text
										lineHeight="21px"
										fontSize="16px"
										fontWeight="600"
										color="brand.700"
									>
										{earmoldLabel}
									</Text>
								)}
							</Flex>
							<AccordionIcon />
						</Flex>
					</AccordionButton>
					<AccordionPanel>
						{[
							EarmoldCategory.HEARING_AID,
							EarmoldCategory.PROTECTION,
							EarmoldCategory.COMM_AND_MONITORING,
							EarmoldCategory.OTHER,
						].map((category) => (
							<EarmoldsSectionPerCategory
								key={category}
								category={category}
								earmolds={earmoldPerStatus?.[category]}
								value={selectedEarmold}
								setValue={async (earmold: Earmold | null | undefined) => {
									if (!earmold) return;

									const variants = await fetchEarmoldVariants(earmold.id);
									setEarmold(earmold.id.toString(), variants ?? undefined);
								}}
							/>
						))}
					</AccordionPanel>
				</>
			)}
		</AccordionItem>
	);
};
